import {
  conditionMeta,
  isValuelessCondition,
  needsSecondValue,
} from "@/shared/components/visualization/Panel/LogPanel/columnFilters/conditionMeta";
import type { LogPanelFilterEdit } from "@/shared/services/AiAgentThreadPubSubBus/models";

type FilterStage = LogPanelFilterEdit["stages"][number];

/** A `replace` with no stages deletes the column's filter. */
export function isClearEdit(edit: LogPanelFilterEdit): boolean {
  return edit.mode === "replace" && edit.stages.length === 0;
}

/**
 * One stage as the user reads it: the condition's label and, where it takes
 * them, its values. Values are quoted so padding and empty strings show.
 */
function describeStage(stage: FilterStage): string {
  const label = conditionMeta[stage.condition].label.toLowerCase();
  if (isValuelessCondition(stage.condition)) {
    return label;
  }
  if (needsSecondValue(stage.condition)) {
    return `${label} "${stage.value}" and "${stage.value2 ?? ""}"`;
  }
  return `${label} "${stage.value}"`;
}

/**
 * What one edit does to its column, as the approval card says it: "clear X",
 * "set X to …", or "add … to X", naming each condition and value so the user
 * approves the filter itself rather than a count of conditions.
 *
 * The and/or setting is named only when the call gives one. Without it the
 * column keeps the setting it already has, which the card cannot see, so the
 * stages are listed with commas instead of guessing a joiner.
 */
export function describeFilterEdit(
  target: string,
  edit: LogPanelFilterEdit,
): string {
  if (isClearEdit(edit)) {
    return `clear ${target}`;
  }
  const joiner = edit.combinator === undefined ? ", " : ` ${edit.combinator} `;
  const stages = edit.stages.map(describeStage).join(joiner);
  return edit.mode === "append"
    ? `add ${stages} to ${target}`
    : `set ${target} to ${stages}`;
}
